/**
 * API hooks for POS cash box closure
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { conns } from '@cfg/axiosConfig';
import type {
  PosBoxToClose,
  PosClosureRequest,
  PosClosureResponse,
  ToleranceConfig,
} from '../types/closure';

/**
 * Fetch cash boxes that require closure
 */
export function useBoxesToClose(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['pos', 'boxes-to-close'],
    queryFn: async (): Promise<PosBoxToClose[]> => {
      // Usamos la clave definida en UrlScheme: erp -> pos_closure -> boxes_to_close
      const response = await conns<PosBoxToClose[]>('erp:pos_closure:boxes_to_close').get();
      return response.data;
    },
    enabled: options?.enabled ?? true,
  });
}

/**
 * Fetch tolerance configuration
 */
export function useTolerance() {
  return useQuery({
    queryKey: ['pos', 'closure', 'tolerance'],
    queryFn: async (): Promise<ToleranceConfig> => {
      // Clave: erp -> pos_closure -> tolerance
      const response = await conns<ToleranceConfig>('erp:pos_closure:tolerance').get();
      return response.data;
    },
  });
}

/**
 * Close cash boxes
 */
export function useCloseCashBoxes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: PosClosureRequest): Promise<PosClosureResponse> => {
      // Clave: erp -> pos_closure -> close
      const response = await conns<PosClosureResponse>('erp:pos_closure:close').post(request);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['pos', 'boxes-to-close'] });
      queryClient.invalidateQueries({ queryKey: ['cashflow'] });
    },
  });
}
